import type { ReaderEvent } from '@shared/events';

export type VendorValue = string | number | boolean;

export interface VendorEvent {
	name: string;
	params: Record<string, VendorValue>;
}

export interface VendorMapping {
	/** Canonical event name → the name this vendor is sent. */
	events?: Partial<Record<ReaderEvent['name'], string>>;
	/** Canonical parameter name → the name this vendor is sent. */
	params?: Record<string, string>;
}

/**
 * Titles are for a person reading the vendor's own screens, never for a
 * report (`system` joins titles on the ids). So they are not sent as
 * parameters or dimensions: a destination puts `titleOf(event)` in the
 * vendor's own title field where it has one, and nowhere otherwise.
 */
const TITLE_FIELDS = new Set(['issue_title', 'article_title']);

/** The most specific title the event carries: the article's, else the issue's. */
export function titleOf(event: ReaderEvent): string | undefined {
	return event.article_title ?? event.issue_title;
}

/**
 * The one place a canonical event becomes a vendor's event. Every field of
 * the row is carried across under its own name unless the vendor's mapping
 * renames it, so a destination only states its exceptions and the catalog
 * never bends to one vendor's rules. The titles are left out (see above).
 */
export function toVendorEvent(
	event: ReaderEvent,
	mapping: VendorMapping = {},
): VendorEvent {
	const { name, ...fields } = event;
	const params: Record<string, VendorValue> = {};
	for (const [key, value] of Object.entries(fields)) {
		if (value === undefined || TITLE_FIELDS.has(key)) {
			continue;
		}
		params[mapping.params?.[key] ?? key] = value;
	}
	return { name: mapping.events?.[name] ?? name, params };
}
